import { pluralize } from "../lib/text.js";
import { ArmyData, ArmyType } from "../schema/latest.js";
import { Ruleset } from "./ruleset.js";
import { uniqueById } from "./unique-by-id.js";
import { Unit } from "./unit.js";
import { sortByName } from "./utils.js";
import { ArmyValidationError } from "./validation.js";

export class Army {
  readonly id: string;
  readonly description: string;
  readonly createdTs: number;
  readonly updatedTs: number;
  readonly shareId: string | null;
  readonly units: Unit[];
  readonly data: ArmyData;
  readonly issues: ArmyValidationError[];
  readonly ruleset: Ruleset;
  readonly type: ArmyType;
  readonly isShared: boolean;

  constructor(ruleset: Ruleset, data: ArmyData, options?: { isShared?: boolean }) {
    this.ruleset = ruleset;
    this.id = data.id;
    this.type = data.type ?? "ARMY";
    this.description = data.description;
    this.createdTs = data.created_ts;
    this.updatedTs = data.updated_ts;
    this.shareId = data.share_id;
    this.data = data;
    this.issues = [];
    this.units = data.units
      .map((unitData) => new Unit(this, unitData))
      .sort((a, b) => Unit.sortByRank(a, b));
    this.isShared = !!options?.isShared;

    this.validateArmy();
  }

  static readonly FALLBACK_NAME = "Unnamed Army";
  static readonly FALLBACK_COLLECTION_NAME = "Unnamed Collection";

  /**
   * Returns the name of an army data object, or the correct fallback.
   *
   * This is useful to construct a human-readable name in places where the full Army instance is
   * not instantiated, and so the `name` getter cannot be used.
   */
  static getNameWithFallback(data: ArmyData) {
    const isCollection = data.type === "COLLECTION";
    const fallback = isCollection ? Army.FALLBACK_COLLECTION_NAME : Army.FALLBACK_NAME;
    return data.name || fallback;
  }

  get name() {
    const fallback = this.isCollection ? Army.FALLBACK_COLLECTION_NAME : Army.FALLBACK_NAME;
    return this.data.name || fallback;
  }

  get mainImage() {
    return this.data.images?.at(0) ?? null;
  }

  get posterImage() {
    return this.data.posterImages?.at(0) ?? null;
  }

  get points() {
    return this.units.reduce((sum, unit) => sum + unit.points * unit.count, 0);
  }

  get unitsCount() {
    return this.units.reduce((sum, unit) => sum + unit.count, 0);
  }

  get isCollection() {
    return this.type === "COLLECTION";
  }

  get typeLabel() {
    return (
      this.isShared ?
        this.isCollection ?
          `Shared Collection`
        : `Shared Army`
      : this.isCollection ? "Collection"
      : "Army"
    );
  }

  get shortTypeLabel() {
    return this.isCollection ? "Collection" : "Army";
  }

  get summary() {
    const units = this.unitsCount === 0 ? "No units" : pluralize(this.unitsCount, "unit", "units");

    // Type collection doesn't show points, so we exclude them from the summary.
    if (this.isCollection) {
      return `Collection, ${units}`;
    }

    // Only show points if there are some units
    if (this.unitsCount > 0) {
      return `${this.points}pts, ${units}`;
    }

    return units;
  }

  get cursedArtefactsDefinitions() {
    return uniqueById(this.units.flatMap((u) => u.cursedArtefacts)).sort(sortByName);
  }

  get keywordsDefinitions() {
    return uniqueById(this.units.flatMap((u) => u.keywords.map((k) => k.definition))).sort(
      sortByName,
    );
  }

  get abyssalAllegiancesDefinitions() {
    return uniqueById(
      this.units.flatMap((u) => u.abyssalAllegiances.map((a) => a.definition)),
    ).sort(sortByName);
  }

  getUnit(unitId: string) {
    return this.units.find((u) => u.id === unitId);
  }

  isValid(this: Army) {
    return this.issues.length === 0;
  }

  private validateArmy(this: Army) {
    // Only validate general and standard if not a collection
    if (!this.isCollection) {
      let hasGeneral = false;
      let hasBattleStandard = false;

      for (const unit of this.units) {
        if (unit.hasKeyword("general")) {
          hasGeneral = true;
        }

        if (unit.hasKeyword("battle_standard")) {
          hasBattleStandard = true;
        }
      }

      if (!hasGeneral) {
        this.issues.push({ type: "MISSING_GENERAL" });
      }

      if (!hasBattleStandard) {
        this.issues.push({ type: "MISSING_BATTLE_STANDARD" });
      }
    }

    const invalidUnits = this.units.filter((unit) => !unit.isValid());

    if (invalidUnits.length > 0) {
      this.issues.push({ type: "INVALID_UNITS", units: invalidUnits });
    }
  }
}
